import ServicePageLayout from './ServicePageLayout';
import { FileText, Mic, Calculator, TrendingUp, Route, FileCheck, Award, Scale, Users, Heart } from 'lucide-react';

const relatedServices = [
  { path: '/droit-social/', label: 'Expertise Droit Social' },
  { path: '/formation-rh/', label: 'Formations RH' },
  { path: '/audit-rh/', label: 'Audit RH' },
  { path: '/accompagnement-collectivites/', label: 'Accompagnement Collectivités' },
];

const services = [
  {
    icon: FileText,
    title: 'Optimisation du CV et lettre de motivation',
    description: 'Rédaction ou amélioration de votre CV pour le rendre clair, attractif et adapté aux attentes des recruteurs. J\'aide également à l\'optimisation de votre profil LinkedIn pour une visibilité accrue sur le marché du travail.',
  },
  {
    icon: Mic,
    title: 'Formation à la prise de parole',
    description: 'Apprenez à gérer le stress et à parler avec confiance en public. Je vous accompagne sur la structuration de votre discours pour être clair et percutant, avec des entraînements aux entretiens d\'embauche et présentations professionnelles.',
  },
  {
    icon: Calculator,
    title: 'Compréhension du bulletin de salaire',
    description: 'Explication détaillée des cotisations et prélèvements sur votre fiche de paie. J\'analyse avec vous les erreurs fréquentes et vous conseille pour les corriger, tout en vous expliquant vos droits liés au salaire.',
  },
  {
    icon: TrendingUp,
    title: 'Coaching pour négociations salariales',
    description: 'Préparation complète à vos négociations salariales : analyse du marché, construction des arguments, simulations d\'entretiens et stratégies pour aborder la négociation avec confiance et maximiser vos chances d\'obtenir une revalorisation.',
  },
  {
    icon: Route,
    title: 'Accompagnement à la reconversion professionnelle',
    description: 'Identification de vos compétences transférables et de vos aspirations profondes. J\'explore avec vous les opportunités de formation et les financements possibles, puis nous construisons un plan d\'action concret pour réussir votre transition.',
  },
  {
    icon: FileCheck,
    title: 'Appui dans les démarches administratives',
    description: 'Aide à la rédaction de courriers professionnels (demande de congé, rupture conventionnelle, réclamation). Je vous explique vos droits du travail et vous accompagne pour constituer un dossier solide en cas de litige avec votre employeur.',
  },
  {
    icon: Award,
    title: 'Diagnostic de compétences',
    description: 'Identification de vos compétences clés et de vos talents naturels à travers un bilan complet. J\'évalue les écarts avec le poste ou le secteur visé et vous propose un plan d\'action personnalisé pour développer vos atouts.',
  },
  {
    icon: Scale,
    title: 'Formation en droit du travail pour salariés',
    description: 'Explication des bases du droit du travail (contrat, licenciement, congés, heures supplémentaires). Je vous sensibilise aux évolutions légales impactant les salariés et vous apprends à défendre vos droits efficacement.',
  },
];

export default function ConseilRHPage() {
  return (
    <ServicePageLayout
      title="Conseil et Accompagnement RH pour Particuliers | Aurore RH"
      description="Accompagnement RH pour particuliers : optimisation CV, coaching entretien, négociation salariale et reconversion professionnelle."
      canonical="https://aurorerh.fr/conseil-rh/"
      relatedServices={relatedServices}
    >
      {/* Introduction */}
      <div className="prose dark:prose-invert max-w-none mb-16">
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Vous traversez une période de transition professionnelle ? Vous vous posez des questions sur votre carrière, 
          votre rémunération ou vos droits en tant que salarié ? En tant que consultante RH indépendante, 
          je vous accompagne avec bienveillance et expertise pour faire de ces défis des opportunités de croissance.
        </p>
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Mon approche est entièrement personnalisée : je prends le temps de comprendre votre situation unique avant 
          de vous proposer des solutions concrètes et adaptées. Que vous soyez en recherche d'emploi, en poste et 
          souhaitant évoluer, ou confronté à une situation professionnelle complexe, je suis là pour vous aider 
          à y voir clair et à avancer sereinement.
        </p>
      </div>

      {/* Services détaillés */}
      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-10">
        Nos prestations pour particuliers
      </h2>

      <div className="space-y-8 mb-16">
        {services.map((service, index) => (
          <div
            key={index}
            className="flex gap-5 p-6 bg-white dark:bg-warm-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-aurore-light/20 dark:border-warm-gray-700"
          >
            <div className="flex-shrink-0 w-14 h-14 bg-aurore-light/40 dark:bg-warm-gray-700 rounded-xl flex items-center justify-center">
              <service.icon className="w-7 h-7 text-aurore-dark dark:text-aurore-light" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">
                {service.title}
              </h3>
              <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Pour qui */}
      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Pour qui sont ces services ?
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-16">
        {[
          'Salariés en reconversion professionnelle',
          'Jeunes diplômés à la recherche de leur premier emploi',
          'Professionnels souhaitant négocier une augmentation',
          'Personnes confrontées à un litige avec leur employeur',
          'Salariés voulant comprendre leur fiche de paie',
          'Toute personne ayant des questions sur le droit du travail',
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-4 bg-warm-gray-50 dark:bg-warm-gray-800/50 rounded-xl">
            <Users className="w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0" />
            <span className="text-warm-gray-700 dark:text-warm-gray-300">{item}</span>
          </div>
        ))}
      </div>

      {/* Méthodologie */}
      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Notre méthodologie d'accompagnement
      </h2>
      <div className="space-y-6 mb-16">
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-aurore-dark text-white rounded-full flex items-center justify-center font-bold">1</div>
          <div>
            <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-1">Diagnostic initial gratuit</h3>
            <p className="text-warm-gray-700 dark:text-warm-gray-300">Un échange de 30 minutes pour comprendre votre situation, vos objectifs et identifier ensemble la meilleure approche.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-aurore-dark text-white rounded-full flex items-center justify-center font-bold">2</div>
          <div>
            <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-1">Proposition personnalisée</h3>
            <p className="text-warm-gray-700 dark:text-warm-gray-300">Je vous propose un accompagnement sur mesure, avec des objectifs clairs et un calendrier défini ensemble.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-aurore-dark text-white rounded-full flex items-center justify-center font-bold">3</div>
          <div>
            <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-1">Accompagnement régulier</h3>
            <p className="text-warm-gray-700 dark:text-warm-gray-300">Des séances de suivi pour ajuster la stratégie, répondre à vos questions et célébrer vos avancées.</p>
          </div>
        </div>
      </div>

      {/* Témoignage */}
      <div className="bg-gradient-to-br from-aurore-pale to-aurore-light/30 dark:from-warm-gray-800 dark:to-warm-gray-800 rounded-2xl p-8 mb-16">
        <div className="flex items-start gap-4">
          <Heart className="w-8 h-8 text-aurore-dark dark:text-aurore-light flex-shrink-0 mt-1" />
          <div>
            <blockquote className="text-lg text-warm-gray-800 dark:text-warm-gray-200 italic leading-relaxed mb-4">
              "Grâce à Aurore, j'ai pu négocier une augmentation de 15% et surtout, j'ai appris à valoriser mes compétences. 
              Son accompagnement bienveillant et son expertise m'ont donné confiance en moi."
            </blockquote>
            <cite className="text-warm-gray-600 dark:text-warm-gray-400 not-italic font-medium">
              — Marie L., Responsable Marketing
            </cite>
          </div>
        </div>
      </div>
    </ServicePageLayout>
  );
}
